// CompraSuelta.jsx
//
// #1887 · sumar jornadas adicionales con Flow (el nombre «Jornada adicional» es del 31-oct-2026; los identificadores internos siguen como «suelta») (spec `.claude/specs/jornada-suelta/`, T27). Mockup APROBADO:
// `tools/mockups/jornada_boton/pago_corregido/jornada_suelta_pago.pdf` (390): pantalla 2 elegir tipo y cantidad, pantalla 3
// «Tus documentos» (la pieza de #1809, `PasoDocumentos`), pantalla 4 pago único con la orden dentro de la casilla (H4).
// El orden es «Tus documentos · Confirmas» (en los planes, «Suscripción»; R73 de #1887), el mismo de los planes (#1809, Sebastián, 31-oct-2026): los papeles van
// primero y después se elige y se confirma la compra (los dos pasos de elegir y confirmar son el paso 2 del indicador).
//
// Un solo componente para los tres lugares donde se compra: dentro de `PlanesYPago` (pantalla de bloqueo y «Elegir plan»,
// tras tocar «No quiero un plan»), y en el diálogo global que abren el chip «Comprar jornada» y `jornadas-agotadas`.
// NADA de plata ni de reglas se decide acá: precios, cantidades posibles, fecha de vencimiento, texto de la orden y su
// hash salen de `GET /me/suelta/`; si hay que pedir documentos lo dice `GET /me/checkout/` (`documentos.exige`). El total que
// se muestra es unitario x cantidad solo para pintarlo; el que se cobra lo recalcula y exige el servidor (el hash de la orden
// lo ata a tipo, cantidad y precio: 409 `orden_cambio` si ya no calza).
// Cubre R33, R34, R73 — .claude/specs/jornada-suelta/
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { documentosDelCheckout } from "../../helpers/documentos-pago";
import { SOPORTE } from "../../helpers/soporte";
import { comprarSuelta, diaMes, horasTxt, nombreTipoPlural, pesos } from "../../helpers/suelta-api";
import { estadoActualDeJornada, recargarOferta } from "../../hooks/useJornadaEstado";
import PasoDocumentos, { DocumentosEnviados, PasosPago } from "../checkout/PasoDocumentos";
import { botonPrimarioSx } from "../checkout/botonesSx";

const TIPOS = ["12h", "24h"];

// «La activas tú con un botón. Dura 12 horas (13 con la hora de cortesía).» Las dos tarjetas dicen igual la activación
// desde P26 (decisión de la central, 31-oct, R32 v8); el mockup decía otra cosa en la de 12 h.
const DESCRIPCION = {
  "12h": "La activas tú con un botón. Dura 12 horas (13 con la hora de cortesía).",
  "24h": "La activas tú con un botón. Dura 24 horas (25 con la hora de cortesía).",
};

const mensajeDeError = (code) => {
  switch (code) {
    case "orden_cambio":
      return "Las condiciones de la orden cambiaron recién. Revisa el texto nuevo y vuelve a aceptarlo.";
    case "suelta_saldo_max":
      return "Ya tienes guardadas todas las jornadas adicionales que se pueden tener a la vez. Usa algunas y vuelves a sumar.";
    case "cantidad_invalida":
      return "Esa cantidad ya no está disponible. Elige otra.";
    case "orden_requerida":
      return "Marca la casilla de la orden para continuar.";
    case "en_prueba":
      return "Las jornadas adicionales se suman al terminar tu prueba gratis.";
    case "suelta_no_aplica":
    case "riel_no_habilitado":
      return `Las jornadas adicionales no están disponibles para tu cuenta por ahora. Escríbenos a ${SOPORTE} si necesitas ayuda.`;
    case "correo_invalido":
      return `Flow no aceptó tu correo. Revisa tu correo en tu perfil y vuelve a intentarlo, o escríbenos a ${SOPORTE}.`;
    case "flow_error":
      return "No pudimos abrir el pago con Flow. No se cobró nada. Intenta de nuevo en un momento.";
    default:
      return `No pudimos iniciar el pago. Intenta de nuevo o escríbenos a ${SOPORTE}.`;
  }
};

// El texto de la orden lo arma el servidor. Acá solo se resaltan las frases que no hay que perderse (la de «no se devuelve»)
// y se enlazan los Términos de Uso y la política de reembolsos (/reembolsos.html, otra pestaña). Si el texto no trae esas frases, se muestra tal cual.
const RUTA_REEMBOLSOS = "(?:https?:\\/\\/[^\\s]*?)?\\/reembolsos\\.html";
const RE_RUTA_REEMBOLSOS = new RegExp(`^${RUTA_REEMBOLSOS}$`);
const RE_ENLACES = new RegExp(`(Términos de Uso|la política de reembolsos|${RUTA_REEMBOLSOS})`);
export const TextoOrden = ({ texto }) => {
  const partes = String(texto || "").split(/((?:Esta jornada adicional no se devuelve|Estas jornadas adicionales no se devuelven)[^.]*\.)/);
  return (
    <span data-testid="suelta-orden-texto">
      {partes.map((parte, i) => {
        if (i % 2 === 1) {
          return (
            <Box key={i} component="strong" data-testid="suelta-orden-no-se-devuelve" sx={{ color: "var(--color-warning)", fontWeight: 700 }}>
              {parte}
            </Box>
          );
        }
        return parte.split(RE_ENLACES).map((trozo, j) => {
          if (trozo === "Términos de Uso") {
            return (
              <a key={`${i}-${j}`} href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                Términos de Uso
              </a>
            );
          }
          if (trozo === "la política de reembolsos" || RE_RUTA_REEMBOLSOS.test(trozo)) {
            // Si el back viejo manda la ruta cruda (https://…/reembolsos.html o /reembolsos.html), se muestra igual el nombre.
            return (
              <Box
                key={`${i}-${j}`}
                component="a"
                data-testid="suelta-orden-reembolsos"
                href="/reembolsos.html"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "var(--color-secondary)", textDecoration: "underline" }}
              >
                la política de reembolsos
              </Box>
            );
          }
          return <span key={`${i}-${j}`}>{trozo}</span>;
        });
      })}
    </span>
  );
};

const enlaceSx = {
  border: 0,
  background: "none",
  p: 0,
  font: "inherit",
  fontSize: 12.2,
  color: "var(--color-info)",
  textDecoration: "underline",
  cursor: "pointer",
  textAlign: "left",
};

const Redondo = ({ children, ...rest }) => (
  <Box
    component="button"
    type="button"
    {...rest}
    sx={{
      width: 38,
      height: 38,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontSize: 20,
      lineHeight: 1,
      cursor: "pointer",
      color: "var(--text-primary)",
      backgroundColor: "transparent",
      border: "1px solid var(--border-strong)",
      "&:hover:not(:disabled)": { backgroundColor: "rgba(var(--fg-rgb), 0.06)" },
      "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
    }}
  >
    {children}
  </Box>
);

/**
 * @param {object} p
 * @param {object} p.oferta           `GET /me/suelta/`
 * @param {object} [p.estadoCheckout] `GET /me/checkout/` (los documentos que se exigen); sin él no hay paso de documentos
 * @param {() => void} [p.onRecargarCheckout]  pide de nuevo el checkout (tras subir documentos, o si el POST los pide)
 * @param {() => void} [p.onVolver]   «Volver a los planes» (dentro de `PlanesYPago`); sin él, «Cancelar»
 * @param {() => void} [p.onCancelar]
 * @param {boolean} [p.docsYaEnviados] los acaba de subir en el bloque de planes (`PlanesYPago`): el indicador de pasos y el aviso «Documentos enviados» siguen a la vista
 */
const CompraSuelta = ({ oferta, estadoCheckout = null, onRecargarCheckout, onVolver, onCancelar, docsYaEnviados = false }) => {
  const productos = useMemo(() => (oferta?.productos || []).filter((p) => TIPOS.includes(p.tipo)), [oferta]);
  const primero = productos.find((p) => p.cantidad_max > 0) || productos[0] || null;
  const [tipo, setTipo] = useState(primero?.tipo || "12h");
  const [cantidad, setCantidad] = useState(1);
  const [paso, setPaso] = useState(1); // 1 elige · 3 confirma (los documentos, si se piden, van ANTES, son el paso 1 del indicador y salen de `pideDocs`)
  const [docsEnviados, setDocsEnviados] = useState(Boolean(docsYaEnviados));
  const [acepta, setAcepta] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [error, setError] = useState("");
  const [pagoEnCurso, setPagoEnCurso] = useState(false);

  const producto = productos.find((p) => p.tipo === tipo) || null;
  // Las cantidades que de verdad se pueden pedir (el back las manda ordenadas): con una compra pendiente propia, solo la
  // misma cantidad. Sin la lista (contrato sin la clave) se usa 1..cantidad_max.
  const cantidades = useMemo(() => {
    if (!producto) return [];
    if (Array.isArray(producto.cantidades) && producto.cantidades.length) return producto.cantidades;
    return Array.from({ length: Number(producto.cantidad_max) || 0 }, (_, i) => i + 1);
  }, [producto]);
  const maxCant = cantidades.length ? Math.max(...cantidades) : 0;
  const minCant = cantidades.length ? Math.min(...cantidades) : 0;

  // Si cambia lo que se puede pedir (otro tipo, la oferta se recargó), la cantidad vuelve al rango válido.
  useEffect(() => {
    if (!cantidades.length) return;
    // Se queda con la más cercana hacia abajo (4 de 12 h pasa a 2 de 24 h si el máximo de 24 h es 2), o con la primera posible.
    if (!cantidades.includes(cantidad)) setCantidad([...cantidades].filter((n) => n <= cantidad).pop() ?? cantidades[0]);
  }, [cantidades, cantidad]);

  const orden = oferta?.ordenes?.[tipo]?.[String(cantidad)] || null;
  // Cambiar el texto (otro tipo, otra cantidad) invalida la aceptación anterior: lo que se acepta es EXACTAMENTE lo que se cobra.
  useEffect(() => {
    setAcepta(false);
  }, [orden?.hash]);

  const docs = documentosDelCheckout(estadoCheckout);
  const pideDocs = docs.exige && !docsEnviados;
  const docsEnRevision = docsEnviados || docs.estado === "enviados";
  // Sin papeles que pedir no hay un paso intermedio que explicar: el indicador «Tus documentos · Confirmas» no sale (igual que los planes).
  const flujoDocs = docs.exige || docsEnviados;

  if (!oferta?.disponible || !productos.length) return null;

  const unitario = Number(producto?.precio_bruto) || 0;
  const total = unitario * cantidad;
  const nombre = (n) => nombreTipoPlural(tipo, n);
  const conLista = (p) => Number(p.precio_lista_bruto) > Number(p.precio_bruto);
  const etiquetaPrecio = oferta.cohorte_precio === "fundador" ? "Precio fundador" : "Precio de lista";

  const seguir = () => {
    setError("");
    setPaso(3);
  };

  const pagar = async () => {
    if (!orden || !acepta || pagando) return;
    setPagando(true);
    setError("");
    const r = await comprarSuelta({ tipo, cantidad, ordenHash: orden.hash });
    if (r.ok && r.data?.url_pago) {
      // El pago vive en flow.cl; vuelve a `/perfil?pago=suelta` (`RetornoSuelta`).
      window.location.assign(r.data.url_pago);
      return;
    }
    setPagando(false);
    setError(mensajeDeError(r.code));
    setPagoEnCurso(false);
    // Lo que se muestra pudo haber cambiado (precio por fecha, saldo, orden): se pide de nuevo la oferta y se vuelve a aceptar.
    if (["orden_cambio", "suelta_saldo_max", "cantidad_invalida", "suelta_no_aplica", "en_prueba"].includes(r.code)) {
      setAcepta(false);
      const recarga = recargarOferta();
      // El back también responde 409 `suelta_saldo_max` cuando lo que frena es una compra PENDIENTE propia (su tope cuenta las
      // pendientes pagables). Ahí «ya tienes el máximo de jornadas» sería mentira: se dice que hay un pago en curso, con su enlace
      // (la pantalla de vuelta de Flow, que sondea el estado). Cubre O1 del revisor final del back.
      if (r.code === "suelta_saldo_max") {
        Promise.resolve(recarga).then(() => {
          if (estadoActualDeJornada().oferta?.ultima?.estado === "pendiente") {
            setError("");
            setPagoEnCurso(true);
          }
        });
      }
    }
    if (r.code === "documentos_requeridos") {
      // El admin los rechazó entre tanto, o nunca llegaron: vuelve el paso de documentos.
      setDocsEnviados(false);
      setPaso(1);
      if (onRecargarCheckout) onRecargarCheckout();
      setError("");
    }
  };

  const pie = onVolver ? (
    <Box component="button" type="button" data-testid="suelta-volver" onClick={onVolver} sx={enlaceSx}>
      Volver a los planes
    </Box>
  ) : (
    <Box component="button" type="button" data-testid="suelta-cancelar" onClick={onCancelar} sx={enlaceSx}>
      Cancelar
    </Box>
  );

  // ── Paso 1 del indicador · tus documentos (la pieza de #1809, tal cual), ANTES de elegir ──────────
  if (pideDocs) {
    return (
      <Box data-testid="suelta-compra" data-paso={1}>
        <PasoDocumentos
          documentos={docs}
          rotuloPaso2="Confirmas"
          onEnviados={() => {
            setDocsEnviados(true);
            if (onRecargarCheckout) onRecargarCheckout();
            setPaso(1);
          }}
        />
        <Box sx={{ mt: 1.25 }}>{pie}</Box>
      </Box>
    );
  }

  // ── Paso 3 · pago único con la orden dentro de la casilla ────────────────────────────────────────
  if (paso === 3) {
    return (
      <Box data-testid="suelta-compra" data-paso={3}>
        {flujoDocs && <PasosPago paso={2} rotuloPaso2="Confirmas" />}
        <Typography sx={{ fontSize: 19, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.3 }}>Último paso</Typography>
        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mt: 0.5, mb: 1.75 }}>Pagas una vez. No queda ningún cobro mensual.</Typography>
        <Box
          data-testid="suelta-resumen"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            p: "12px 14px",
            mb: 1.5,
            borderRadius: "13px",
            backgroundColor: "rgba(var(--color-primary-rgb), 0.12)",
            border: "1px solid rgba(var(--color-primary-rgb), 0.45)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 700, fontSize: 14.5, color: "var(--text-primary)" }}>{nombre(cantidad)}</Box>
            <Box sx={{ fontSize: 11.5, color: "var(--text-muted)", mt: 0.25 }}>{`${pesos(unitario)} cada una · IVA incluido`}</Box>
          </Box>
          <Box data-testid="suelta-total" sx={{ fontWeight: 800, fontSize: 22, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {pesos(total)}
          </Box>
        </Box>
        {docsEnRevision && <DocumentosEnviados recien={docsEnviados} />}
        {orden ? (
          <Box
            component="label"
            data-testid="suelta-orden"
            sx={{
              display: "flex",
              gap: 1.1,
              alignItems: "flex-start",
              fontSize: 12.3,
              lineHeight: 1.5,
              color: "var(--text-tertiary)",
              mb: 1.5,
              p: "10px 12px",
              borderRadius: "12px",
              backgroundColor: "var(--bg-input)",
              border: "1px solid var(--border-soft)",
              cursor: "pointer",
              "& a": { color: "var(--color-info)" },
            }}
          >
            <Box
              component="input"
              type="checkbox"
              data-testid="suelta-orden-casilla"
              checked={acepta}
              onChange={(e) => setAcepta(e.target.checked)}
              sx={{ mt: "3px", accentColor: "var(--color-primary)", flex: "0 0 auto" }}
            />
            <TextoOrden texto={orden.texto} />
          </Box>
        ) : (
          <Typography data-testid="suelta-sin-orden" sx={{ fontSize: 12.5, color: "var(--text-secondary)", mb: 1.5 }}>
            Esa cantidad ya no está disponible. Vuelve y elige otra.
          </Typography>
        )}
        <Button
          data-testid="suelta-pagar"
          variant="contained"
          disableElevation
          fullWidth
          disabled={!orden || !acepta || pagando}
          onClick={pagar}
          sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14 }}
        >
          {pagando ? "Abriendo Flow..." : `Continuar en Flow · ${pesos(total)}`}
        </Button>
        {error && (
          <Typography data-testid="suelta-error" role="alert" sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1 }}>
            {error}
          </Typography>
        )}
        {pagoEnCurso && (
          <Typography data-testid="suelta-pago-en-curso" role="alert" sx={{ fontSize: 12.5, color: "var(--text-secondary)", mt: 1, lineHeight: 1.5 }}>
            <b>Tienes un pago en curso.</b> Mientras no se confirme o venza, no se puede sumar otra jornada adicional.{" "}
            <a data-testid="suelta-pago-en-curso-link" href="/perfil?pago=suelta" style={{ color: "var(--color-info)" }}>
              Ver cómo va
            </a>
          </Typography>
        )}
        <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: 1.25, lineHeight: 1.5 }}>Te llevamos a Flow para completar con tarjeta y vuelves aquí.</Typography>
        <Box component="button" type="button" data-testid="suelta-cambiar" onClick={() => setPaso(1)} sx={{ ...enlaceSx, display: "block", mt: 1.25 }}>
          Volver
        </Box>
      </Box>
    );
  }

  // ── Paso 1 · eliges tipo y cantidad ──────────────────────────────────────────────────────────────
  return (
    <Box data-testid="suelta-compra" data-paso={1}>
      {flujoDocs && <PasosPago paso={2} rotuloPaso2="Confirmas" />}
      <Typography sx={{ fontSize: 19, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.3 }}>Suma solo lo que vas a usar</Typography>
      <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mt: 0.5, mb: 1.75, lineHeight: 1.5 }}>
        Sin suscripción. Quedan guardadas y las usas cuando tengas turno.
      </Typography>

      <Box role="radiogroup" aria-label="Tipo de jornada" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.5 }}>
        {productos.map((p) => {
          const activo = p.tipo === tipo;
          const agotada = !(p.cantidad_max > 0);
          return (
            <Box
              key={p.tipo}
              component="button"
              type="button"
              role="radio"
              aria-checked={activo}
              disabled={agotada}
              data-testid={`suelta-opcion-${p.tipo}`}
              data-precio={p.precio_bruto}
              onClick={() => setTipo(p.tipo)}
              sx={{
                // Un <button> centra su contenido en vertical: sin esto la tarjeta sin elegir arranca más abajo que la elegida.
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                textAlign: "left",
                cursor: agotada ? "not-allowed" : "pointer",
                p: "12px 14px",
                borderRadius: "13px",
                fontFamily: "inherit",
                color: "var(--text-primary)",
                opacity: agotada ? 0.55 : 1,
                backgroundColor: activo ? "rgba(var(--color-primary-rgb), 0.12)" : "var(--bg-input)",
                border: `1px solid ${activo ? "var(--color-primary)" : "transparent"}`,
                "&:hover:not(:disabled)": { borderColor: activo ? "var(--color-primary)" : "var(--border-strong)" },
                "&:focus-visible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 2 },
              }}
            >
              <Box sx={{ fontSize: 13.5, fontWeight: 700, color: activo ? "var(--color-primary-soft)" : "var(--text-muted)" }}>{`Jornada de ${horasTxt(p.tipo)}`}</Box>
              <Box sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2, fontVariantNumeric: "tabular-nums", mt: 0.25 }}>{pesos(p.precio_bruto)}</Box>
              {conLista(p) && (
                <Box sx={{ fontSize: 12, color: "var(--text-faint)", mb: 0.75 }}>
                  <Box component="span" sx={{ textDecoration: "line-through" }}>{pesos(p.precio_lista_bruto)}</Box> lista
                </Box>
              )}
              <Box sx={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                {agotada ? "Pasaría tu tope de jornadas adicionales guardadas." : DESCRIPCION[p.tipo]}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box
        data-testid="suelta-cantidad"
        data-valor={cantidad}
        data-max={maxCant}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, p: "12px 14px", mb: 1.5, borderRadius: "13px", backgroundColor: "var(--bg-input)" }}
      >
        <Box>
          <Box sx={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>¿Cuántas?</Box>
          <Box sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>{`Hasta ${maxCant}`}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Redondo data-testid="suelta-cantidad-menos" aria-label="Una menos" disabled={cantidad <= minCant} onClick={() => setCantidad((n) => Math.max(minCant, n - 1))}>
            −
          </Redondo>
          <Box data-testid="suelta-cantidad-valor" aria-live="polite" sx={{ minWidth: 28, textAlign: "center", fontSize: 24, fontWeight: 800, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
            {cantidad}
          </Box>
          <Redondo data-testid="suelta-cantidad-mas" aria-label="Una más" disabled={cantidad >= maxCant} onClick={() => setCantidad((n) => Math.min(maxCant, n + 1))}>
            +
          </Redondo>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1.5, mb: 1.25 }}>
        <Box sx={{ fontSize: 14, color: "var(--text-secondary)" }}>{nombre(cantidad)}</Box>
        <Box data-testid="suelta-total" sx={{ fontSize: 28, fontWeight: 800, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
          {pesos(total)}
        </Box>
      </Box>

      <Box
        data-testid="suelta-vence"
        sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: "10px 12px", mb: 1.75, borderRadius: "12px", border: "1px solid var(--border-soft)" }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: "var(--accent-onboarding)", mt: "1px" }} />
        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>
          Duran{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>{`${oferta.vigencia_dias || 60} días`}</Box>
          {` desde que pagas.${oferta.vence_si_pagas_hoy ? ` Si pagas hoy, hasta el ${diaMes(oferta.vence_si_pagas_hoy)}.` : ""}`}
        </Typography>
      </Box>

      <Button
        data-testid="suelta-seguir"
        variant="contained"
        disableElevation
        fullWidth
        disabled={!cantidades.length}
        onClick={seguir}
        sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14 }}
      >
        Seguir
      </Button>
      <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: 1.25, lineHeight: 1.5, textAlign: "center" }}>
        {`${etiquetaPrecio}, IVA incluido. `}
        {pie}
      </Typography>
    </Box>
  );
};

export default CompraSuelta;
